import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Eye, Plus, Shield, UserCog, Users as UsersIcon } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/components/ui/Toast";
import { usePageTitle } from "@/lib/title";
import type { Role, User } from "@/types";
import { Card, CardBody } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { FullPageSpinner } from "@/components/ui/Spinner";
import { EmptyState } from "@/components/ui/EmptyState";

function CreateUserModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("student");
  const [error, setError] = useState<string | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: () => api.post("/api/v1.0/users", { email, full_name: fullName, password, role }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Failed to create user"),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutate();
  };

  return (
    <Modal title="Add user" onClose={onClose}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Input label="Full name" required value={fullName} onChange={(e) => setFullName(e.target.value)} />
        <Input label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input label="Temporary password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-slate-700">Role</label>
          <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            <option value="student">Student</option>
            <option value="instructor">Instructor</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isPending}>
            Create user
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function roleTone(role: Role) {
  if (role === "admin") return "red" as const;
  if (role === "instructor") return "blue" as const;
  return "slate" as const;
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function StatCard({ icon: Icon, label, value }: { icon: typeof UsersIcon; label: string; value: number | string }) {
  return (
    <Card>
      <CardBody className="flex items-center gap-3 py-3.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="text-lg font-semibold leading-tight text-slate-900">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </CardBody>
    </Card>
  );
}

const roleFilters: Array<{ label: string; value: Role | "" }> = [
  { label: "All roles", value: "" },
  { label: "Admins", value: "admin" },
  { label: "Instructors", value: "instructor" },
  { label: "Students", value: "student" },
];

export function UsersPage() {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { user: currentUser, impersonate } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<Role | "">("");
  const [showCreate, setShowCreate] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["users", { search, roleFilter }],
    queryFn: () =>
      api.getPaginated<User>(
        `/api/v1.0/users?page=1&page_size=50&search=${encodeURIComponent(search)}&role=${roleFilter}`,
      ),
  });

  const { data: roleCounts } = useQuery({
    queryKey: ["users", "role-counts"],
    queryFn: async () => {
      const [admins, instructors, students] = await Promise.all([
        api.getPaginated<User>("/api/v1.0/users?page=1&page_size=1&role=admin"),
        api.getPaginated<User>("/api/v1.0/users?page=1&page_size=1&role=instructor"),
        api.getPaginated<User>("/api/v1.0/users?page=1&page_size=1&role=student"),
      ]);
      return { admin: admins.meta.total, instructor: instructors.meta.total, student: students.meta.total };
    },
  });

  const { mutate: updateStatus } = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) => api.patch(`/api/v1.0/users/${id}`, { status }),
    onSuccess: () => {
      notify("User updated");
      void queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const onImpersonate = async (u: User) => {
    setImpersonatingId(u.id);
    try {
      await impersonate(u.id);
      notify(`Now viewing as ${u.full_name}`);
      navigate("/");
    } catch (err) {
      notify(err instanceof ApiError ? err.message : "Failed to impersonate user", "error");
    } finally {
      setImpersonatingId(null);
    }
  };

  usePageTitle("Users", {
    searchValue: search,
    onSearchChange: setSearch,
    searchPlaceholder: "Search by name or email...",
  });

  const totalUsers = data?.meta.total ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Manage accounts, roles and access.</p>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" /> Add user
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard icon={UsersIcon} label="Total users" value={totalUsers} />
        <StatCard icon={Shield} label="Admins" value={roleCounts?.admin ?? "—"} />
        <StatCard icon={UserCog} label="Instructors" value={roleCounts?.instructor ?? "—"} />
        <StatCard icon={UsersIcon} label="Students" value={roleCounts?.student ?? "—"} />
      </div>

      <div className="flex items-center gap-2">
        {roleFilters.map((f) => (
          <button
            key={f.value}
            onClick={() => setRoleFilter(f.value)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              roleFilter === f.value ? "bg-brand-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <FullPageSpinner />
      ) : !data?.data.length ? (
        <EmptyState icon={UsersIcon} title="No users found" description="Try adjusting your search or role filter." />
      ) : (
        <Card>
          <CardBody className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                <tr>
                  <th className="px-5 py-3">User</th>
                  <th className="px-5 py-3">Role</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Joined</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.data.map((u) => {
                  const isSelf = u.id === currentUser?.id;
                  const canImpersonate = u.role !== "admin" && !isSelf && u.status === "active";
                  return (
                    <tr key={u.id} className="hover:bg-slate-50/60">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-semibold text-brand-700">
                            {initials(u.full_name)}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-medium text-slate-800">
                              {u.full_name}
                              {isSelf && <span className="ml-1.5 text-xs font-normal text-slate-400">(you)</span>}
                            </p>
                            <p className="truncate text-xs text-slate-500">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <Badge tone={roleTone(u.role)}>{u.role}</Badge>
                      </td>
                      <td className="px-5 py-3">
                        <Badge tone={u.status === "active" ? "green" : "amber"}>{u.status}</Badge>
                      </td>
                      <td className="px-5 py-3 text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {canImpersonate && (
                            <Button
                              size="sm"
                              variant="ghost"
                              loading={impersonatingId === u.id}
                              onClick={() => onImpersonate(u)}
                              title={`View app as ${u.full_name}`}
                            >
                              <Eye className="h-3.5 w-3.5" /> View as
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => updateStatus({ id: u.id, status: u.status === "active" ? "suspended" : "active" })}
                          >
                            {u.status === "active" ? "Suspend" : "Reactivate"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
